import { useState } from 'react';
import { siteInfo } from '@/mocks/data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/feature/SEO';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      setFormMessage('您的留言已成功提交，我们会尽快回复您。');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      if (honeypot === '') {
        formData.delete('phone_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9q863vnahb7382ov7eg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言！我们会在24小时内回复您。');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试或通过邮箱联系我们。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试或通过邮箱联系我们。');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    description: siteInfo.description,
    url: 'https://adultvideoguide.cn',
    email: siteInfo.email,
    foundingDate: siteInfo.founded,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: siteInfo.email,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于我们 - 成人短视频指南"
        description={`${siteInfo.name} - ${siteInfo.description}成立于${siteInfo.founded}，致力于为成年用户提供专业、可靠的成人短视频平台推荐与安全观看指导。`}
        keywords="关于我们,品牌介绍,联系方式,成人短视频指南"
        canonicalUrl="/about"
        schema={orgSchema}
      />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Hero Banner */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mx-auto mb-6">
              <i className="ri-information-line text-2xl"></i>
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">关于我们</h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {siteInfo.name}成立于{siteInfo.founded}，是一个致力于为18岁以上成年用户提供成人短视频平台专业推荐与安全观看指导的综合性指南网站。
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
                  品牌介绍
                </span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  我们的使命
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  在成人短视频内容日益丰富的今天，用户面临着信息过载、平台质量参差不齐、隐私安全风险等诸多挑战。
                  我们深知这些困扰，因此创建了这个平台，致力于为成年用户提供经过严格筛选的合规平台推荐和全面的安全指导。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  我们的团队由内容安全专家、隐私保护顾问和技术评测专家组成，每一位成员都对成人内容领域有着深入的研究和丰富的实践经验。
                  我们坚持客观、公正、专业的原则，对所有推荐平台进行多维度评测。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们的使命是帮助每一位成年用户在安全合规的前提下，理性地探索和享受成人短视频内容，
                  同时保护好自己的隐私和数字安全。
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20professional%20team%20workspace%20with%20warm%20amber%20and%20cream%20tones%2C%20clean%20modern%20aesthetic%2C%20abstract%20geometric%20shapes%20representing%20trust%20and%20professionalism%2C%20soft%20neutral%20background%2C%20premium%20editorial%20style%2C%20warm%20ambient%20lighting&width=600&height=450&seq=about-brand-01&orientation=landscape"
                  alt="品牌使命与专业团队"
                  className="w-full h-72 md:h-80 rounded-xl object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                核心价值
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">我们坚持的原则</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-shield-check-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">安全至上</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  所有推荐平台均经过严格的安全审核，确保用户数据得到充分保护。
                </p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-scales-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">客观公正</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  基于多维度评测标准，提供客观中立的平台评价和推荐，不偏袒任何平台。
                </p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-4">
                  <i className="ri-user-heart-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">用户为本</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  一切内容从用户需求出发，提供实用、易懂的指导和建议。
                </p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-file-text-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">合规为先</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  严格遵守国家法律法规，所有内容均符合合规要求，杜绝违法违规信息。
                </p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-refresh-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">持续更新</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  紧跟行业动态，持续更新平台评测和内容指南，确保信息的时效性和准确性。
                </p>
              </div>

              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-4">
                  <i className="ri-lock-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">隐私保护</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们自身严格保护访客隐私，不收集不必要的个人信息，不使用追踪器。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-4">
                行业信誉
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">值得信赖的内容来源</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 bg-background-100 rounded-xl border border-background-200/70">
                <div className="text-3xl font-heading font-bold text-primary-500 mb-2">500+</div>
                <p className="text-sm text-foreground-600">深度评测文章</p>
              </div>
              <div className="text-center p-6 bg-background-100 rounded-xl border border-background-200/70">
                <div className="text-3xl font-heading font-bold text-accent-500 mb-2">50+</div>
                <p className="text-sm text-foreground-600">平台深度评测</p>
              </div>
              <div className="text-center p-6 bg-background-100 rounded-xl border border-background-200/70">
                <div className="text-3xl font-heading font-bold text-primary-500 mb-2">100万+</div>
                <p className="text-sm text-foreground-600">累计阅读量</p>
              </div>
              <div className="text-center p-6 bg-background-100 rounded-xl border border-background-200/70">
                <div className="text-3xl font-heading font-bold text-accent-500 mb-2">2024</div>
                <p className="text-sm text-foreground-600">网站成立年份</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-4">
                <i className="ri-mail-line text-xl"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                有任何疑问、建议或合作意向？请填写下表，我们会在24小时内回复。
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                >
                  <option value="">请选择主题</option>
                  <option value="platform-recommend">平台推荐咨询</option>
                  <option value="content-question">内容相关问题</option>
                  <option value="cooperation">商务合作</option>
                  <option value="feedback">意见反馈</option>
                  <option value="other">其他</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                  placeholder="请输入您的留言内容（500字符以内）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                className="antispam-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {formStatus === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    发送留言
                  </>
                )}
              </button>

              {formStatus === 'success' && (
                <div className="flex items-start gap-3 p-4 bg-accent-50 border border-accent-200 rounded-lg">
                  <i className="ri-checkbox-circle-fill text-accent-500 text-lg flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-accent-800">{formMessage}</p>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="flex items-start gap-3 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                  <i className="ri-error-warning-fill text-primary-500 text-lg flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-primary-800">{formMessage}</p>
                </div>
              )}
            </form>

            <div className="mt-8 text-center">
              <p className="text-sm text-foreground-500">
                或直接发送邮件至：
                <a href={`mailto:${siteInfo.email}`} className="text-primary-600 hover:text-primary-700 font-medium ml-1">
                  {siteInfo.email}
                </a>
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}